/**
 * Interactions and transcripts (S08, S09).
 *
 * An interaction is a call that has been *analysed*. Not every call becomes one:
 * a call nobody answered has nothing to analyse, and a call that failed has no
 * transcript at all. That is why this list is not simply "calls" — the page is
 * about what was learned from a conversation, and a row with no conclusions in
 * it would be a row an operator cannot act on.
 */
import { z } from 'zod';

import { contactSummarySchema } from './queue.js';
import {
  LEAD_CATEGORY_FILTERS,
  LeadCategory,
  NextAction,
  TranscriptRole,
  UserIntent,
} from './enums.js';

export const transcriptTurnSchema = z.object({
  seq: z.number().int().min(0),
  role: TranscriptRole.schema,
  content: z.string(),
  /**
   * Informational only. Consecutive turns routinely share a second, so the
   * order of the list is `seq` and never this.
   */
  spokenAt: z.string().datetime().nullable(),
});
export type TranscriptTurn = z.infer<typeof transcriptTurnSchema>;

/**
 * Sentiment, as the analyzer reports it.
 *
 * Both are plain numbers rather than a formatted string, because the UI colours
 * the score by sign and draws the magnitude as a bar — decisions that need the
 * value, not a rendering of it.
 */
export const sentimentSchema = z.object({
  /** −1.0 (hostile) to +1.0 (delighted). */
  score: z.number().min(-1).max(1),
  /** 0.0 to 1.0: how strongly, regardless of direction. */
  magnitude: z.number().min(0).max(1),
  /** The analyzer's own word for the tone, when it offered one. */
  toneLabel: z.string().nullable(),
});
export type Sentiment = z.infer<typeof sentimentSchema>;

/** One row in the list. */
export const interactionSchema = z.object({
  id: z.string().uuid(),
  contact: contactSummarySchema,
  leadCategory: LeadCategory.schema,
  userIntent: UserIntent.schema,
  nextAction: NextAction.schema,
  sentiment: sentimentSchema,
  /** The analyzer's summary. Null when it produced none. */
  summary: z.string().nullable(),
  /**
   * When the call happened, for display.
   *
   * Null when the call row has since been deleted — the interaction survives it
   * (`call_id` is `SET NULL`), because what was learned outlives the record of
   * the attempt.
   */
  callAt: z.string().datetime().nullable(),
  /** When we analysed it. Authoritative for ordering; see the list query. */
  analyzedAt: z.string().datetime(),
});
export type Interaction = z.infer<typeof interactionSchema>;

/** One interaction with everything the drawer needs. */
export const interactionDetailSchema = interactionSchema.extend({
  email: z.string().nullable(),
  /** Empty when the call was answered but the transcript could not be read. */
  transcript: z.array(transcriptTurnSchema),
  /**
   * Non-empty when the analyzer's output had to be mapped to a safe default —
   * an unrecognised category, a score out of range. Shown so a strange-looking
   * row can be explained rather than doubted.
   */
  mappingWarnings: z.array(z.string()),
});
export type InteractionDetail = z.infer<typeof interactionDetailSchema>;

export const interactionListQuerySchema = z.object({
  /** Matched against the contact's name, company, phone and the summary. */
  search: z.string().trim().max(120).optional(),
  /**
   * One category, from the four the filter offers.
   *
   * `UNKNOWN` is deliberately not among them: it means the analyzer could not
   * decide, and offering it as a filter would present indecision as a finding.
   */
  category: z.enum(LEAD_CATEGORY_FILTERS).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(10),
});
export type InteractionListQuery = z.infer<typeof interactionListQuerySchema>;

export const interactionListSchema = z.object({
  items: z.array(interactionSchema),
  total: z.number().int().min(0),
});
export type InteractionList = z.infer<typeof interactionListSchema>;
